import EpdSelectComponent from 'components/form/EpdSelectComponent';
import HelpIcon from 'components/icons/HelpIcon';
import arrowDown from 'images/arrow-down.png';
import arrowUp from 'images/arrow-up.png';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionState, EPDModel } from 'services/EpdClient';
import { InfoBox, Input } from 'styles/Styles.styled';
import { DataSourceOptionModel, Option } from 'types/types';

import { FixedLabelTop, FixedRow } from '../../../pages/wizard/styles';
import { Validation } from '../../../pages/wizard/Validation';

import {
  AddButton,
  ArrowDownBox,
  ArrowImage,
  ArrowUpBox,
  Box,
  MaterialColumn3,
  MaterialGrid,
  ReferenceColumn,
  RemoveButton,
} from '../styles';

import { FixedTextInputComponent } from 'components/form/FixedTextInputComponent';
import { useEpdDataSourceOptions } from 'services/api/queries';

interface Props {
  epd: EPDModel | undefined;
  validation: any;
  infoArrowItems: Record<string, boolean>;
  toggleOpen: (field: string) => void;
  onChangeEpd: (field: string, value: any) => void;
  SelectStyle: any;
  isFetching: boolean;
}

export const DatasourcesSection: React.FC<Props> = ({
  epd,
  validation,
  infoArrowItems,
  toggleOpen,
  onChangeEpd,
  SelectStyle,
  isFetching,
}) => {
  const { t } = useTranslation();
  const isDisabled = epd?.epdDataSourcesState?.actionState === ActionState.Disabled || isFetching;
  const [rowDataSourceValues, setDataSourceRowValues] = React.useState<any[]>([]);
  const dataSourceOptionsSource = useEpdDataSourceOptions().data;

  const dataSourceOptions = dataSourceOptionsSource
    ?.filter((x) => !x.isDisabled)
    .filter((x) => x.dataSourceVersions && x.dataSourceVersions.some((v) => !v.isDisabled))
    .map((x: DataSourceOptionModel) => {
      return {
        value: x.id,
        label: x.name,
      } as Option;
    });

  const getDataSourceVersionOptions = (dataSourceId?: string) => {
    if (!dataSourceId) {
      return [] as Option[];
    }

    const dataSource = dataSourceOptionsSource?.find(
      (x: DataSourceOptionModel) => x.id.toLowerCase() === dataSourceId.toLowerCase()
    );

    return dataSource?.dataSourceVersions;
  };

  useEffect(() => {
    const fetchDataSourcesRows = async () => {
      setDataSourceRowValues(epd?.epdDataSources ?? []);
    };
    fetchDataSourcesRows();
  }, []);

  useEffect(() => {
    setDataSourceRowValues(epd?.epdDataSources ?? []);
  }, [epd?.epdDataSources]);

  const saveDataSourceRowValues = (values: any[]) => {
    const valuesToSave: any = values.map((row) => {
      return {
        Id: row.id,
        DataSource: { id: row.dataSource.id, name: row.dataSource.name },
        DataSourceVersion: { id: row.dataSourceVersion.id, name: row.dataSourceVersion.name },
        IsMain: row.isMain,
      };
    });
    onChangeEpd('DataSourcesProperties', valuesToSave);
  };

  const createDataSourceRow = () => {
    const newRowValues = [
      ...rowDataSourceValues,
      {
        id: undefined,
        dataSource: { id: undefined, name: '' },
        dataSourceVersion: { id: undefined, name: '' },
        isMain: false,
      },
    ];
    setDataSourceRowValues((prev) => newRowValues);
  };

  const deleteDataSourceRow = (index: number) => {
    let newValues = [...rowDataSourceValues];
    newValues.splice(index, 1);
    setDataSourceRowValues((prev) => [...newValues]);
    saveDataSourceRowValues(newValues);
  };

  const onChangeDataSource = (data: any, index: number, propertyName: string) => {
    const newValues = rowDataSourceValues.map((row) => ({
      ...row,
      dataSource: { ...row.dataSource },
      dataSourceVersion: { ...row.dataSourceVersion },
    }));

    if (propertyName === 'IsMain' && data === true) {
      const updatedValues = newValues.map((row, i) => ({
        ...row,
        isMain: i === index,
      }));

      setDataSourceRowValues(updatedValues);
      saveDataSourceRowValues(updatedValues);
      return;
    }

    const updatedRow = { ...newValues[index] };

    // Handle property updates based on the propertyName
    switch (propertyName) {
      case 'DataSource':
        updatedRow.dataSource = { id: data?.value, name: data?.label };
        updatedRow.dataSourceVersion = { id: undefined, name: '' };
        break;
      case 'DataSourceVersion':
        updatedRow.dataSourceVersion = { id: data?.value, name: data?.label };
        break;
      case 'IsMain':
        updatedRow.isMain = data;
        break;
      default:
        throw new Error(`Unhandled property: ${propertyName}`);
    }

    // Replace the updated row in the newValues array
    newValues[index] = updatedRow;

    setDataSourceRowValues(newValues);
    saveDataSourceRowValues(newValues);
  };

  return (
    <FixedRow style={{ marginBottom: '1rem' }} state={epd?.epdDataSourcesState?.actionState}>
      <FixedLabelTop>
        {t('epdWizard.wizardStep2.properties.dataSources')}
        <HelpIcon content={t('epdWizard.wizardStep2.tooltips.dataSources')} />
        <Validation
          validation={validation}
          epd={epd}
          field={['epdDataSources', 'otherDatabaseUsedAsDatasourceDescription']}
        />
      </FixedLabelTop>
      <Box style={{ paddingTop: '0.5rem' }}>
        <div style={{ width: '100%', paddingBottom: '1rem' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 0', marginBottom: 0, borderLeft: 'none' }}>
            {infoArrowItems.dataSources ? (
              <ArrowUpBox>{t('epdWizard.wizardStep2.info.dataSources')}</ArrowUpBox>
            ) : (
              <ArrowDownBox>{t('epdWizard.wizardStep2.info.dataSources')}</ArrowDownBox>
            )}
            {t('epdWizard.wizardStep2.info.dataSources').length >= 73 &&
              (infoArrowItems.dataSources ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('dataSources')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('dataSources')} />
              ))}
          </InfoBox>
        </div>
        <MaterialGrid>
          {rowDataSourceValues.map((item, index) => (
            <React.Fragment key={index}>
              <ReferenceColumn style={{ padding: '5px 0', display: 'grid', rowGap: '3px' }}>
                <EpdSelectComponent
                  options={dataSourceOptions}
                  value={item?.dataSource?.id ? { value: item?.dataSource?.id, label: item?.dataSource?.name } : null}
                  isDisabled={isDisabled}
                  styles={SelectStyle}
                  isClearable
                  placeholder={t('placeholder.defaultSelectDatabase')}
                  onChange={(e: any) => onChangeDataSource(e as any, index, 'DataSource')}
                  error={item?.dataSource?.isDisabled && 'Please select a valid database'}
                />
                <EpdSelectComponent
                  options={getDataSourceVersionOptions(item.dataSource?.id)?.filter((x) => !x.isDisabled) ?? []}
                  value={
                    item?.dataSourceVersion?.id
                      ? { value: item?.dataSourceVersion?.id, label: item?.dataSourceVersion?.name }
                      : null
                  }
                  isDisabled={!item?.dataSource?.id || isDisabled}
                  styles={SelectStyle}
                  isClearable
                  placeholder={t('placeholder.defaultSelectDatabaseVersion')}
                  onChange={(e: any) => onChangeDataSource(e as any, index, 'DataSourceVersion')}
                  error={item?.dataSourceVersion?.isDisabled && 'Please select a valid database version'}
                />
                <label style={{ fontSize: '14px', userSelect: 'none', display: 'flex', alignItems: 'center' }}>
                  <Input
                    type="checkbox"
                    style={{ margin: '0 5px 0 0' }}
                    checked={item?.isMain}
                    disabled={isDisabled}
                    onChange={(e: any) => onChangeDataSource(e.target.checked as any, index, 'IsMain')}
                  />
                  Set as main database
                </label>
              </ReferenceColumn>
              <MaterialColumn3 style={{ textAlign: 'center', alignItems: 'start', alignSelf: 'start' }}>
                <RemoveButton type="button" onClick={() => deleteDataSourceRow(index)} disabled={item?.isMain || isDisabled}>
                  {t('remove')}
                </RemoveButton>
              </MaterialColumn3>
            </React.Fragment>
          ))}

          <MaterialColumn3>
            {rowDataSourceValues.length < 6 && (
              <AddButton type="button" onClick={createDataSourceRow} disabled={isDisabled}>
                + {t('add')}
              </AddButton>
            )}
          </MaterialColumn3>

          <ReferenceColumn style={{ display: 'grid' }}>
            <label style={{ fontSize: '14px', userSelect: 'none', display: 'flex', alignItems: 'center' }}>
              <Input
                type="checkbox"
                style={{ marginRight: '5px', marginBottom: '0.5rem' }}
                checked={epd?.isOtherDatabaseUsedAsDatasource ?? false}
                disabled={epd?.isOtherDatabaseUsedAsDatasourceState?.actionState === ActionState.Disabled}
                onChange={(e: any) => onChangeEpd('isOtherDatabaseUsedAsDatasource', e.target.checked)}
              />
              Other database
            </label>
            {epd?.isOtherDatabaseUsedAsDatasource && (
              <FixedTextInputComponent
                style={{ margin: 0 }}
                value={epd?.otherDatabaseUsedAsDatasourceDescription}
                propertyName={'otherDatabaseUsedAsDatasourceDescription'}
                onChangeEpd={(fieldName: string, value: any) => {
                  onChangeEpd(fieldName, value);
                }}
                disabled={epd?.otherDatabaseUsedAsDatasourceDescriptionState?.actionState === ActionState.Disabled}
                placeholder="Type here..."
              />
            )}
          </ReferenceColumn>
        </MaterialGrid>
      </Box>
    </FixedRow>
  );
};

export default DatasourcesSection;
